import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { AtSign, Bell, ChevronRight, Menu, ChevronDown, Check, MessageSquare, UserCheck, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAccessControl } from "@/hooks/useAccessControl";
import { UserIdentityAvatar } from "@/components/ui/UserIdentityAvatar";
import { useBreadcrumbLabels } from "@/components/layout/Layout";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@/hooks/useApi";
import type { AppNotification } from "@/types/api";

const breadcrumbMap: Record<string, string> = {
  dashboard: "nav.dashboard",
  agenda: "nav.agenda",
  operations: "nav.operations",
  programs: "nav.programs",
  artists: "nav.artists",
  data: "nav.day",
  items: "nav.items",
  categories: "nav.categories",
  locations: "nav.locations",
  stock: "nav.stock",
  movements: "nav.movements",
  recipients: "nav.recipients",
  shipments: "nav.shipments",
  kits: "nav.kits",
  "offline-actions": "nav.offlineActions",
  audit: "nav.audit",
  imports: "nav.imports",
  users: "nav.users",
  new: "common.new",
  timeline: "nav.timeline",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)  return "agora";
  if (mins < 60) return `há ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `há ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "ontem";
  return `há ${days} dias`;
}

function NotifIcon({ type }: { type: string }) {
  const cls = "w-4 h-4 shrink-0";
  if (type === "assigned_owner") return <UserCheck className={cls} />;
  if (type === "assigned_team")  return <Users className={cls} />;
  if (type === "new_message")    return <MessageSquare className={cls} />;
  if (type === "mention")        return <AtSign className={cls} />;
  return <Bell className={cls} />;
}

// ── Notification dropdown ─────────────────────────────────────────────────────

function NotificationPanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const notifications = data?.notifications ?? [];

  async function handleClick(notif: AppNotification) {
    if (!notif.is_read) {
      await markRead.mutateAsync(notif.id);
    }
    onClose();
    if (notif.link) navigate(notif.link);
  }

  return (
    <div
      className="absolute right-0 top-full mt-1 w-[360px] bg-white border border-black/15 shadow-lg z-50"
      style={{ maxHeight: "480px", display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 shrink-0">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-black/60">
          Notificações
          {(data?.unread_count ?? 0) > 0 && (
            <span className="ml-2 bg-[#d9ed09] text-black text-[10px] font-extrabold px-1.5 py-0.5 rounded-sm">
              {data!.unread_count}
            </span>
          )}
        </span>
        {(data?.unread_count ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => markAll.mutateAsync()}
            className="text-[10px] font-semibold text-black/45 hover:text-black flex items-center gap-1 transition-colors"
          >
            <Check size={11} />
            Marcar tudo como lido
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-black/40">Carregando…</div>
        ) : notifications.length === 0 ? (
          <div className="py-10 text-center">
            <Bell className="w-8 h-8 mx-auto text-black/20 mb-2" />
            <div className="text-sm text-black/40">Nenhuma notificação</div>
          </div>
        ) : (
          notifications.map((notif) => (
            <button
              key={notif.id}
              type="button"
              onClick={() => handleClick(notif)}
              className={[
                "w-full text-left px-4 py-3 flex items-start gap-3 border-b border-black/[0.06] transition-colors hover:bg-[#FAFAF7]",
                !notif.is_read ? "bg-[rgba(217,237,9,0.06)]" : "",
              ].join(" ")}
            >
              {/* Icon */}
              <div
                className={[
                  "mt-0.5 shrink-0 p-1.5 rounded-sm",
                  !notif.is_read ? "bg-[#d9ed09] text-black" : "bg-black/[0.06] text-black/50",
                ].join(" ")}
              >
                <NotifIcon type={notif.type} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className={`text-[12px] leading-snug ${!notif.is_read ? "font-semibold" : "font-medium text-black/70"}`}>
                  {notif.title}
                </div>
                {notif.body && (
                  <div className="text-[11px] text-black/55 mt-0.5 truncate">
                    {notif.body}
                  </div>
                )}
                <div className="text-[10px] text-black/35 mt-1">
                  {relativeTime(notif.created_at)}
                  {notif.actor_name && (
                    <> · <span className="font-medium text-black/50">{notif.actor_name}</span></>
                  )}
                </div>
              </div>

              {/* Unread dot */}
              {!notif.is_read && (
                <div className="w-2 h-2 rounded-full bg-[#d9ed09] mt-1.5 shrink-0 ring-1 ring-black/20" />
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2.5 border-t border-black/10 text-center shrink-0">
          <Link
            to="/operations"
            onClick={onClose}
            className="text-[11px] font-semibold text-black/50 hover:text-black transition-colors"
          >
            Ver Kanban →
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Main Topbar ───────────────────────────────────────────────────────────────

export function Topbar({ onOpenNav }: { onOpenNav?: () => void } = {}) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { appUser, roleLabel } = useAccessControl();
  const location = useLocation();
  const navigate = useNavigate();
  const { labels } = useBreadcrumbLabels();

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { data: notifData } = useNotifications();
  const unreadCount = notifData?.unread_count ?? 0;

  // Close on outside click
  useEffect(() => {
    if (!notifOpen) return;
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [notifOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const segments = location.pathname.split("/").filter(Boolean);

  const displayName =
    appUser?.full_name ?? user?.displayName ?? user?.email?.split("@")[0] ?? "User";

  return (
    <header className="h-16 bg-gl-white border-b border-gl-bg-dark flex items-center px-4 md:px-6 gap-3 md:gap-4 shrink-0">
      {/* Abre a gaveta de navegação — só existe abaixo de md, onde a sidebar
          sai do fluxo. No desktop a sidebar está sempre visível. */}
      <button
        type="button"
        onClick={onOpenNav}
        className="md:hidden -ml-1 p-2 text-gl-text hover:bg-gl-bg-dark transition-colors shrink-0"
        aria-label="Abrir menu de navegação"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Breadcrumb */}
      <nav
        className="flex items-center gap-1.5 text-sm flex-1 min-w-0"
        aria-label="Breadcrumb"
      >
        {segments.map((segment, idx) => {
          const isLast = idx === segments.length - 1;
          const path = "/" + segments.slice(0, idx + 1).join("/");
          const labelKey = breadcrumbMap[segment];
          const label = labels[path] ?? (labelKey ? t(labelKey) : segment);

          return (
            <span key={path} className="flex items-center gap-1.5">
              {idx > 0 && (
                <ChevronRight className="w-3 h-3 shrink-0 text-black/25" />
              )}
              {isLast ? (
                <span className="text-gl-text font-medium truncate">{label}</span>
              ) : (
                <Link
                  to={path}
                  className="text-black/40 hover:text-black/70 transition-colors font-medium"
                >
                  {label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>

      {/* Notifications bell */}
      <div ref={notifRef} className="relative">
        <button
          type="button"
          onClick={() => setNotifOpen((v) => !v)}
          className="relative p-2 hover:bg-gl-bg-dark transition-colors"
          aria-label={t("common.notifications")}
        >
          <Bell className="w-5 h-5 text-gl-text" />
          {unreadCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-[#d9ed09] text-black text-[9px] font-extrabold rounded-sm ring-1 ring-black/20 px-0.5"
              aria-label={`${unreadCount} notificações não lidas`}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {notifOpen && <NotificationPanel onClose={() => setNotifOpen(false)} />}
      </div>

      {/* User menu */}
      <button
        className="flex items-center gap-2.5 hover:bg-gl-bg-dark px-2.5 py-1.5 transition-colors"
        onClick={handleSignOut}
        aria-label={t("common.userMenu")}
      >
        <UserIdentityAvatar
          user={{
            display_name: displayName,
            identity_color: appUser?.identity_color,
            identity_text_color: appUser?.identity_text_color,
          }}
          sizeClassName="w-7 h-7"
          textClassName="text-xs font-bold"
        />
        {/* Nome e papel só a partir de md — no celular fica o avatar, que já
            identifica o usuário e mantém o alvo de toque. */}
        <div className="hidden md:block text-left leading-tight">
          <div className="text-sm font-medium text-gl-text">{displayName}</div>
          <div className="text-[11px] text-black/40">{roleLabel}</div>
        </div>
        <ChevronDown className="hidden md:block w-4 h-4 text-black/35" />
      </button>
    </header>
  );
}
